import { format, formatInSpecificTimeZone } from '@fruitful/fruitils';
import {
    BadgeLabel,
    BodyMediumRegular,
    BodySmallRegular,
    CirclePlayIcon,
    CONTENT_MAX_WIDTH,
    H6Emphasized,
    H6Regular,
    IconButton,
    Image,
    isNonMobileWeb,
    Maximize2Icon,
    PillButton,
    Shadows,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { DEFAULT_SESSION_TIME_ZONE } from '../../guidanceSessions/types/GuidanceSessions';
import type { GuideWithAvailability } from '../types/Guide';

/** 12-hour time in the device locale / local timezone (UTC instants from the server display correctly). */
const SESSION_SLOT_TIME_FORMAT = 'h:mm a';

const formatSessionSlotTime = (
    time: Date,
    displayInClientTimeZone: boolean,
): string => {
    if (displayInClientTimeZone) {
        return format(time, SESSION_SLOT_TIME_FORMAT);
    }
    return formatInSpecificTimeZone(
        time,
        DEFAULT_SESSION_TIME_ZONE,
        SESSION_SLOT_TIME_FORMAT,
    );
};

const imageHeight = isNonMobileWeb ? 120 * 1.1 : 120;
const imageWidth = isNonMobileWeb ? 96 * 1.1 : 96;

const GuideInfo = ({
    firstName,
    styles,
    profileImageUrl,
    onPressDetails,
}: {
    firstName: string;
    styles: string;
    profileImageUrl: string;
    onPressDetails: () => void;
}) => {
    const styleItems =
        styles
            ?.split(',')
            .map((s) => s.trim())
            .filter(Boolean) ?? [];

    return (
        <XStack gap={16} alignItems="flex-start">
            <View
                position="relative"
                width={imageWidth}
                height={imageHeight}
                borderRadius={16}
                overflow="hidden"
                backgroundColor="$gray200"
                onPress={onPressDetails}
                cursor="pointer"
            >
                <Image
                    source={{ uri: profileImageUrl }}
                    width={imageWidth}
                    height={imageHeight}
                    resizeMode="cover"
                />
                <View
                    position="absolute"
                    bottom={8}
                    left={0}
                    right={0}
                    alignItems="center"
                >
                    <PillButton
                        variant="primary"
                        text="Play"
                        icon={CirclePlayIcon}
                        onPress={onPressDetails}
                        pillProps={{
                            opacity: 0.75,
                        }}
                    />
                </View>
            </View>
            <YStack flex={1} gap={16} alignItems="flex-start">
                {isNonMobileWeb ? (
                    <YStack gap={4} paddingTop={4}>
                        <H6Emphasized>{firstName}</H6Emphasized>
                        <BodyMediumRegular color="$gray1000">
                            CFP&reg;
                        </BodyMediumRegular>
                    </YStack>
                ) : (
                    <YStack gap={4}>
                        <H6Regular>{firstName}</H6Regular>
                        <BodySmallRegular color="$gray1000">
                            CFP&reg;
                        </BodySmallRegular>
                    </YStack>
                )}
                <XStack gap={isNonMobileWeb ? 8 : 4} flexWrap="wrap">
                    {styleItems.map((style) => (
                        <BadgeLabel
                            key={style}
                            text={style}
                            variant="neutral"
                            size="regular"
                        />
                    ))}
                </XStack>
            </YStack>
            <View position="absolute" top={0} right={0}>
                <IconButton
                    variant="secondary"
                    icon={Maximize2Icon}
                    onPress={onPressDetails}
                    size="tiny"
                />
            </View>
        </XStack>
    );
};

export const GuideAvailabilityCard = ({
    guide,
    availability,
    displayInClientTimeZone = true,
    onPressDetails,
    onPressTime,
}: {
    guide: GuideWithAvailability | undefined;
    /** ISO UTC Dates; rendered in client local time or Eastern Time per displayInClientTimeZone. */
    availability: Date[];
    displayInClientTimeZone?: boolean;
    onPressDetails: (guide: GuideWithAvailability) => void;
    onPressTime: (guide: GuideWithAvailability, time: Date) => void;
}) => {
    if (guide == null) {
        return null;
    }

    const cellMinWidth = 86;
    const timeCellsPerRow = isNonMobileWeb ? 4 : 3;
    const emptyCellsOnLastRow =
        (timeCellsPerRow - (availability.length % timeCellsPerRow)) %
        timeCellsPerRow;

    return (
        <View position="relative" width="100%" maxWidth={CONTENT_MAX_WIDTH}>
            <YStack
                backgroundColor="$white"
                borderRadius={24}
                padding={16}
                gap={16}
                {...Shadows.MEDIUM}
            >
                <GuideInfo
                    firstName={guide.firstName}
                    styles={guide.styles ?? ''}
                    profileImageUrl={guide.profileImageUrl ?? ''}
                    onPressDetails={() => {
                        onPressDetails(guide);
                    }}
                />
                {/* <ScrollView
                    backgroundColor="$gray100"
                    borderRadius={16}
                    maxHeight={195} // up to 3 rows visible with no scrollbar
                    scrollbarWidth="thin"
                    alwaysBounceVertical={false}
                    contentContainerStyle={{
                        paddingVertical: 16,
                        paddingHorizontal: isNonMobileWeb ? 16 : 12,
                    }}
                > */}
                <View
                    backgroundColor="$gray100"
                    borderRadius={16}
                    paddingVertical={16}
                    paddingHorizontal={isNonMobileWeb ? 16 : 12}
                    width="100%"
                >
                    {availability.length === 0 ? (
                        <BodyMediumRegular textAlign="center">
                            No availability
                        </BodyMediumRegular>
                    ) : (
                        <XStack
                            gap={12}
                            flexWrap="wrap"
                            justifyContent="center"
                            alignItems="center"
                        >
                            {availability.map((time: Date) => (
                                <View
                                    key={time.getTime()}
                                    backgroundColor="$white"
                                    minWidth={cellMinWidth}
                                    borderRadius={16}
                                    padding={12}
                                    borderWidth={1}
                                    borderColor="$gray400"
                                    onPress={() => {
                                        onPressTime(guide, time);
                                    }}
                                    cursor="pointer"
                                    hoverStyle={{
                                        borderColor: '$gray1100',
                                    }}
                                >
                                    <BodySmallRegular textAlign="center">
                                        {formatSessionSlotTime(
                                            time,
                                            displayInClientTimeZone,
                                        )}
                                    </BodySmallRegular>
                                </View>
                            ))}
                            {/* We want the 'grid' to be centered within its parent, which is achieved by justifyContent="center". But that means that if the last row is not 'full', then the cells are centered within the row, rather than left-aligned. With flexbox, the only way to achieve the desired left alignment in this scenario is to render empty cells to fill the row. */}
                            {emptyCellsOnLastRow > 0 &&
                                Array.from({ length: emptyCellsOnLastRow }).map(
                                    (_, i) => (
                                        <View
                                            key={i}
                                            minWidth={cellMinWidth}
                                            aria-hidden={true}
                                            opacity={0}
                                        />
                                    ),
                                )}
                        </XStack>
                    )}
                </View>
                {/* </ScrollView> */}
            </YStack>
        </View>
    );
};
